import * as jd from 'jsdataframe';
import * as moment from 'moment';
import {AssetSelectedModel} from '../_models/asset.model';
import {AssetsService} from './assets.service';
import {BehaviorSubject, forkJoin, Observable, of, Subject} from 'rxjs';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ErrorService} from '../../shared/_services/error.service';
import {HttpClient} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {LoadingService} from '../../shared/_services/loading.service';
import {IM_DF_CUM_SUFFIX, IM_DF_OC_SUFFIX, IM_DF_PNL_SUFFIX, PlantRiskDataModel, PlantRiskModel, PlantRiskOutageModel} from '../_models/plant-risk.model';
import {DateService} from './date.service';
import {SbService} from '../../shared/_services/sb.service';
import {SoService} from '../../shared/_services/so.service';
import {DATE_FORMAT, DateModel} from '../_models/date.model';
import {FuncsService} from '../../shared/_services/funcs.service';

@Injectable({
  providedIn: 'root'
})
export class PlantRiskService implements OnDestroy {

  private pr$ = new BehaviorSubject<PlantRiskModel[]>([]);
  private outage$ = new BehaviorSubject<PlantRiskOutageModel[]>([]);

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private assetsService: AssetsService,
    private dateService: DateService,
    private errorService: ErrorService,
    private httpClient: HttpClient,
    private loadingService: LoadingService,
    private sbService: SbService,
    private soService: SoService,
  ) {
    this.getSo();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getSo(): void {
    this.soService.onNewSoActive()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (so) => {
          if (so !== '') {
            // reset im data to already clean the screen
            this.newPlantRisk([]);
          } else {
            this.callGetPlantRisk();
          }
        }
      );
  }

  private callGetPlantRisk(): void {
    const assets = this.assetsService.getSelAssets();
    const curDate = this.dateService.getNominationDate();
    if (assets?.length && curDate) {
      const loadMsg = 'loading your intramonth data';
      this.loadingService.start(loadMsg);
      this.getPlantRiskHttp(assets, curDate)
        .subscribe(
          (data) => {
            if (data?.length) {
              this.newPlantRisk(data);
            }
            this.loadingService.stop(loadMsg);
          },
          (err) => {
            this.newPlantRisk([]);
            this.loadingService.stop(loadMsg);
            this.sbService.message('Something went wrong loading the data for assets: ' + err.errMessage);
          }
        );
    }
  }

  private getPlantRiskHttp(selAssets: AssetSelectedModel[], date: DateModel): Observable<any> {
    const httpRequests = [];
    for (const asset of selAssets) {
      httpRequests.push(
        this.httpClient.get(
          environment.urlWarp + '/read/plantrisk/' + asset.asset +
          '?start=' + date.date +
          '&stop=' + date.date +
          '&start=' + moment(date.date).add(date.horAsOf, 'day').format(DATE_FORMAT) +
          '&stop=' + moment(date.date).add(date.horUpTo, 'day').format(DATE_FORMAT)
        ).pipe(
          map((r: any) => {
            return <PlantRiskModel>{
              asset: asset.asset,
              assetLabel: asset.assetLabel,
              country: asset.country,
              prData: r.data ? r.data.map((d) => new PlantRiskDataModel(d)) : []
            };
          }),
          catchError(this.errorService.handleError)
        )
      );
    }
    if (!httpRequests.length) {
      return of([]);
    }
    return forkJoin(httpRequests);
  }

  // ********************************************************************************************************
  // CHANGE DATA
  // ********************************************************************************************************

  private buildOutage(pr: PlantRiskModel[]): void {
    const outage: PlantRiskOutageModel[] = [];
    if (pr?.length) {
      const selCountries = FuncsService.distinct(pr.map((a) => a.country));
      for (const country of selCountries) {
        outage.push(new PlantRiskOutageModel(country));
        const countryPr = pr.filter((i) => i.country === country) || [];
        if (countryPr.length) {
          let df = jd.dfFromObjArray(countryPr[0].prData, ['ts', 'cf', 'pnl']);
          df = df.setNames(['ts', countryPr[0].assetLabel + IM_DF_OC_SUFFIX, countryPr[0].assetLabel + IM_DF_PNL_SUFFIX]);
          df = df.updateCols('ts', (colVector) => {
            return colVector.toDtype('string');
          });
          df = df.insertCol(countryPr[0].assetLabel + IM_DF_OC_SUFFIX + IM_DF_CUM_SUFFIX, null);
          df = df.insertCol(countryPr[0].assetLabel + IM_DF_PNL_SUFFIX + IM_DF_CUM_SUFFIX, null);
          if (countryPr.length > 1) {
            for (let i = 1; i < countryPr.length; i++) {
              if (countryPr[i].prData.length) {
                let df2 = jd.dfFromObjArray(countryPr[i].prData, ['ts', 'cf', 'pnl']);
                df2 = df2.setNames(['ts', countryPr[i].assetLabel + IM_DF_OC_SUFFIX, countryPr[i].assetLabel + IM_DF_PNL_SUFFIX]);
                df = df.updateCols('ts', (colVector) => {
                  return colVector.toDtype('string');
                });
                df = df.insertCol(countryPr[i].assetLabel + IM_DF_OC_SUFFIX + IM_DF_CUM_SUFFIX, null);
                df = df.insertCol(countryPr[i].assetLabel + IM_DF_PNL_SUFFIX + IM_DF_CUM_SUFFIX, null);
                df = df.join(df2, 'outer', {
                  'leftBy': ['ts'],
                  'rightBy': ['ts']
                });
              } else {
                // add empty column
                df = df.insertCol(countryPr[i].assetLabel, null);
              }
            }
          }
          outage[outage.length - 1].outageData = df.toObjArray();
        }
      }
    }
    this.newPlantRiskOutage(outage);
  }

  // ********************************************************************************************************
  // BROADCAST DATA
  // ********************************************************************************************************

  private newPlantRisk(data: PlantRiskModel[]) {
    this.pr$.next(data);
    this.buildOutage(data);
  }

  onNewPlantRisk(): Observable<PlantRiskModel[]> {
    return this.pr$.asObservable();
  }

  getPlantRisk(): PlantRiskModel[] {
    return this.pr$.getValue();
  }

  private newPlantRiskOutage(data: PlantRiskOutageModel[]) {
    this.outage$.next(data);
  }

  onNewPlantRiskOutage(): Observable<PlantRiskOutageModel[]> {
    return this.outage$.asObservable();
  }

  getPlantRiskOutage(): PlantRiskOutageModel[] {
    return this.outage$.getValue();
  }

}
